import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, Users, FileText, Factory, Truck, Wallet, AlertTriangle, TrendingUp, TimerOff, PhoneCall, BellRing, MessageSquare, Clock, X, RefreshCw, ArrowUpRight, ClipboardList, CalendarPlus, ThumbsDown, Ban } from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, ComposedChart, Line, Legend } from "recharts";
import { FyComparisonDialog } from "@/components/FyComparison";
import { FollowupsDialog } from "@/components/FollowupsDialog";
import { isNetOnline } from "@/lib/connectivity";
import { ActionCenterStrips, PredictedItemsLine } from "@/components/IntelWidgets";
import { toast } from "sonner";

const kpis = [
  { key: "turnover_fy", label: "Turnover (FY)", icon: TrendingUp, bg: "#3B82F6", hint: "this financial year", currency: true, to: "/invoices" },
  { key: "total_leads", label: "Total Leads", icon: Target, bg: "#10B981", hint: "all enquiries", to: "/leads" },
  { key: "pending_followups", label: "Pending Follow-ups", icon: Users, bg: "#8B5CF6", hint: "silent 3+ working days", to: "/leads" },
  { key: "quotations_sent", label: "Quotations Sent", icon: FileText, bg: "#F97316", hint: "awaiting response", to: "/quotations" },
  { key: "open_sales_orders", label: "Open Sales Orders", icon: ClipboardList, bg: "#6366F1", hint: "awaiting delivery", to: "/orders" },
  { key: "orders_in_production", label: "Active Production", icon: Factory, bg: "#0EA5E9", hint: "active job cards", to: "/production" },
  { key: "dispatch_due_today", label: "Dispatch Due Today", icon: Truck, bg: "#14B8A6", hint: "scheduled shipments", to: "/dispatch" },
  { key: "outstanding_payments", label: "Outstanding", icon: Wallet, bg: "#F43F5E", hint: "receivable balance", currency: true, to: "/payments" },
];

const CountUp = ({ value, format }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const target = Number(value || 0);
    if (!target) { setN(target); return; }
    const dur = 750;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format(Math.round(n))}</>;
};

const MiniBars = ({ color }) => (
  <span className="inline-flex items-end gap-[2px] ml-auto">
    {[5, 9, 7, 12].map((h, i) => (
      <span key={i} className="w-[3px] rounded-sm" style={{ height: h, background: color, opacity: 0.35 + i * 0.18 }} />
    ))}
  </span>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reorderDue, setReorderDue] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const loadReorder = () => api.get("/intel/predicted-orders").then((r) => setReorderDue(r.data)).catch(() => {});
  const load = () => {
    setRefreshing(true);
    return api.get("/dashboard").then((r) => { setData(r.data); setUpdatedAt(new Date()); }).finally(() => setRefreshing(false));
  };
  useEffect(() => {
    load();
    loadReorder();
  }, []);
  const dismissReorder = async (c) => {
    await api.post("/size-intel/reorder-due/snooze", { customer_id: c.customer_id, days: 0, last_purchase: c.last_purchase });
    setReorderDue((l) => l.filter((x) => x.customer_id !== c.customer_id));
  };
  const quoteAction = async (q, what) => {
    try {
      if (what === "extend") {
        const r = await api.patch(`/quotations/${q.id}/validity?days=15`);
        toast.success(`${q.number} validity extended to ${fmtDate(r.data.valid_until)}`);
      } else if (what === "lost") {
        await api.patch(`/quotations/${q.id}/status?status=Lost`);
        toast.success(`${q.number} marked Lost`);
      } else {
        if (!window.confirm(`Cancel quotation ${q.number}?`)) return;
        await api.patch(`/quotations/${q.id}/status?status=Cancelled`);
        toast.success(`${q.number} cancelled`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };
  const snoozeReorder = async (c) => {
    const d = window.prompt(`Remind about ${c.company_name} after how many days?`, "7");
    const days = parseInt(d, 10);
    if (!days || days < 1) return;
    await api.post("/size-intel/reorder-due/snooze", { customer_id: c.customer_id, days });
    setReorderDue((l) => l.filter((x) => x.customer_id !== c.customer_id));
  };
  const [fyOpen, setFyOpen] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  const [online, setOnline] = useState(isNetOnline());
  useEffect(() => {
    const onNet = (e) => {
      const up = !!e.detail?.online;
      setOnline(up);
      if (up) { load(); loadReorder(); }
    };
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("net-status", onNet);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("net-status", onNet); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const monthSales = (data?.monthly_sales || []).slice(-1)[0]?.amount || 0;
  const firstName = user?.name ? user.name.split(" ")[0] : "";

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Status bar */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_12px_rgba(15,23,42,0.04)] px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 fade-up" data-testid="status-bar">
        <span className="relative flex h-2 w-2">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${online ? "bg-emerald-400" : "bg-rose-400"}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${online ? "bg-emerald-500" : "bg-rose-500"}`} />
        </span>
        <span className="text-sm font-semibold text-slate-800">System Status</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${online ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`} data-testid="status-pill">
          {online ? "Online" : "Offline"}
        </span>
        <span className="ml-auto text-xs text-slate-400" data-testid="status-updated">
          Last updated: {updatedAt ? updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
        </span>
        <button onClick={() => { load(); loadReorder(); }} disabled={refreshing}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B82F6] hover:text-[#2563EB] transition-colors duration-200 disabled:opacity-50"
                data-testid="refresh-data-btn">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh Data
        </button>
      </div>

      {/* Welcome banner */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#7C3AED] via-[#8B5CF6] to-[#6366F1] p-6 sm:p-8 text-white shadow-[0_16px_40px_rgba(124,58,237,0.25)]"
        data-testid="welcome-banner">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/[0.07] pointer-events-none" />
        <div className="absolute -bottom-24 right-40 w-56 h-56 rounded-full bg-white/[0.05] pointer-events-none" />
        <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Welcome back{firstName ? `, ${firstName}` : ""}!</h2>
        <p className="text-sm sm:text-[15px] text-white/85 mt-1.5">Here's what's happening at your plant today.</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-white/90">
          <span className="inline-flex items-center gap-1.5" data-testid="banner-production"><span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" /> {data?.orders_in_production ?? 0} order{(data?.orders_in_production ?? 0) === 1 ? "" : "s"} in production</span>
          <span className="inline-flex items-center gap-1.5" data-testid="banner-dispatch"><span className="w-1.5 h-1.5 rounded-full bg-sky-300 inline-block" /> {data?.dispatch_due_today ?? 0} dispatch{(data?.dispatch_due_today ?? 0) === 1 ? "" : "es"} due today</span>
          <span className="inline-flex items-center gap-1.5" data-testid="banner-sales"><span className="w-1.5 h-1.5 rounded-full bg-amber-300 inline-block" /> {inr(monthSales)} sales this month</span>
        </div>
        <div className="hidden sm:flex absolute right-8 top-1/2 -translate-y-1/2 w-24 h-24 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 items-center justify-center">
          <Factory size={40} strokeWidth={1.3} />
        </div>
      </motion.div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map(({ key, label, icon: Icon, bg, hint, currency, to }, i) => {
          const isTurnover = key === "turnover_fy";
          const isFu = key === "pending_followups";
          const card = (
            <Card className="p-5 h-full border-slate-100 rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer bg-white" data-testid={`kpi-${key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] text-slate-500 font-medium truncate">{label}</div>
                  <div className="font-display text-[26px] font-bold text-slate-900 tabular-nums leading-none mt-2.5">
                    {data == null ? <Skeleton className="h-7 w-16" /> : <CountUp value={data[key]} format={currency ? inr : (v) => v} />}
                  </div>
                </div>
                <div className="w-11 h-11 flex-none rounded-xl flex items-center justify-center text-white shadow-lg transition-transform duration-300 hover:scale-110"
                     style={{ background: bg, boxShadow: `0 8px 20px ${bg}45` }}>
                  <Icon size={19} strokeWidth={1.8} />
                </div>
              </div>
              <div className="mt-3.5 flex items-center gap-1.5 text-xs text-slate-400">
                <ArrowUpRight size={13} className="text-emerald-500 flex-none" />
                <span className="truncate">{hint}</span>
                <MiniBars color={bg} />
              </div>
            </Card>
          );
          return (
            <motion.div key={key} initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.45, delay: 0.06 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}>
              {isTurnover ? (
                <div onClick={() => setFyOpen(true)} className="block h-full" data-testid="kpi-link-turnover_fy" title="Click for financial-year comparison">{card}</div>
              ) : isFu ? (
                <div onClick={() => setFuOpen(true)} className="block h-full" data-testid="kpi-link-pending_followups" title="Click to review pending follow-ups">{card}</div>
              ) : (
                <Link to={to} className="block h-full" data-testid={`kpi-link-${key}`}>{card}</Link>
              )}
            </motion.div>
          );
        })}
      </div>
      <FyComparisonDialog open={fyOpen} onOpenChange={setFyOpen} />
      <FollowupsDialog open={fuOpen} onOpenChange={setFuOpen} onChanged={load} />

      <div className="space-y-3 fade-up" style={{ animationDelay: "160ms" }}>
        {data?.overdue_count > 0 && (
          <div className="border border-rose-100 border-l-4 border-l-rose-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="overdue-alert">
            <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm">
              <AlertTriangle size={15} strokeWidth={1.5} />
              {data.overdue_count} overdue invoice{data.overdue_count > 1 ? "s" : ""} — {inr(data.overdue_amount)} pending
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {data.overdue_invoices.map((i) => (
                <Link key={i.id} to="/invoices" className="text-xs bg-rose-50/60 border border-rose-200 rounded-md px-2.5 py-1.5 text-rose-700 hover:bg-rose-100 transition-colors duration-200" data-testid={`overdue-${i.number}`}>
                  <span className="font-mono">{i.number}</span> · {i.customer} · {inr(i.balance)} · due {fmtDate(i.due_date)}
                </Link>
              ))}
            </div>
          </div>
        )}

        {reorderDue.length > 0 && (
          <div className="border border-amber-100 border-l-4 border-l-amber-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="reorder-due">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2.5">
              <BellRing size={15} strokeWidth={1.5} />
              Reorder Due — {reorderDue.length} customer{reorderDue.length > 1 ? "s" : ""} past their usual buying cycle
            </div>
            <div className="flex flex-wrap gap-2">
              {reorderDue.map((c) => (
                <div key={c.customer_id} className="text-xs bg-amber-50/60 border border-amber-200 rounded-md px-2.5 py-1.5 text-amber-900 flex flex-wrap items-center gap-2 w-full" data-testid={`reorder-${c.customer_id}`}>
                  <span><b>{c.company_name}</b> · usually every {c.avg_gap_days}d · last order {fmtDate(c.last_purchase)} · <b className="text-rose-600">{c.overdue_days}d overdue</b> · avg {inr(c.avg_value)}</span>
                  {c.mobile && (
                    <span className="inline-flex items-center gap-1">
                      <a href={`tel:${c.mobile}`} className="p-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800" title={`Call ${c.contact_person || c.company_name}`}><PhoneCall size={11} /></a>
                      <a href={`https://wa.me/91${c.mobile.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hello ${c.contact_person || ""}, greetings from Hi-Tech Silico! It's been a while since your last order — can we help you with a fresh requirement?`)}`}
                         target="_blank" rel="noreferrer" className="p-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700" title="WhatsApp"><MessageSquare size={11} /></a>
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 border-l border-amber-200 pl-1.5 ml-0.5">
                    <button onClick={() => snoozeReorder(c)} className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600" title="Remind after a few days" data-testid={`reorder-snooze-${c.customer_id}`}><Clock size={11} /></button>
                    <button onClick={() => dismissReorder(c)} className="p-1 rounded bg-slate-100 hover:bg-rose-100 hover:text-rose-600 text-slate-600" title="Dismiss this reminder" data-testid={`reorder-dismiss-${c.customer_id}`}><X size={11} /></button>
                  </span>
                  <PredictedItemsLine c={c} />
                </div>
              ))}
            </div>
          </div>
        )}

        <ActionCenterStrips />

        {data?.expiring_quotations?.length > 0 && (
          <div className="border border-violet-100 border-l-4 border-l-[#8B5CF6] bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="expiring-quotes">
            <div className="flex items-center gap-2 text-[#7C3AED] font-semibold text-sm mb-2.5">
              <TimerOff size={15} strokeWidth={1.5} />
              Quotations Expiring — {data.expiring_quotations.length} need attention
            </div>
            <div className="flex flex-wrap gap-2">
              {data.expiring_quotations.map((q) => (
                <div key={q.id}
                     className={`text-xs border rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 transition-colors duration-200 ${q.expired ? "border-rose-200 bg-rose-50/60 text-rose-700" : "border-violet-200 bg-violet-50/60 text-[#7C3AED]"}`}
                     data-testid={`expquote-${q.id}`}>
                  <Link to="/quotations" className="inline-flex items-center gap-1.5 hover:underline">
                    {q.expired && <AlertTriangle size={11} />}
                    <span className="font-mono">{q.number}</span> · {q.customer} · {inr(q.total)} ·
                    <b>{q.expired ? "expired" : "expires"} {fmtDate(q.valid_until)}</b>
                  </Link>
                  <span className="inline-flex items-center gap-0.5 ml-1 pl-1.5 border-l border-current/20">
                    <button onClick={() => quoteAction(q, "extend")} title="Extend validity by 15 days"
                            className="p-1 rounded hover:bg-white/80 text-emerald-700" data-testid={`expquote-extend-${q.id}`}>
                      <CalendarPlus size={13} />
                    </button>
                    <button onClick={() => quoteAction(q, "lost")} title="Mark Lost"
                            className="p-1 rounded hover:bg-white/80 text-amber-700" data-testid={`expquote-lost-${q.id}`}>
                      <ThumbsDown size={13} />
                    </button>
                    <button onClick={() => quoteAction(q, "cancel")} title="Cancel quotation"
                            className="p-1 rounded hover:bg-white/80 text-rose-700" data-testid={`expquote-cancel-${q.id}`}>
                      <Ban size={13} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 fade-up" style={{ animationDelay: "240ms" }}>
        <Card className="lg:col-span-8 p-6 border-slate-100 rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.04)] bg-white" data-testid="monthly-sales-chart">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[15px] font-display font-bold text-slate-900">Monthly Sales & Profit</div>
              <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">Last 6 months <TrendingUp size={13} className="text-[#3B82F6]" strokeWidth={1.5} /></div>
            </div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">INR</div>
          </div>
          <div className="h-64">
            {data && (
              <ResponsiveContainer width="100%" height={256} minWidth={0}>
                <ComposedChart data={data.monthly_sales} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#60A5FA" />
                      <stop offset="100%" stopColor="#2563EB" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(59,130,246,0.06)" }} formatter={(v, name) => [inr(v), name]} contentStyle={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", border: "1px solid rgba(226,232,240,0.8)", borderRadius: 12, fontSize: 12, boxShadow: "0 16px 48px rgba(15,23,42,0.12)" }} />
                  <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }} iconSize={9} />
                  <Bar dataKey="amount" name="Sales" fill="url(#barGrad)" radius={[6, 6, 0, 0]} maxBarSize={48} animationDuration={900} animationEasing="ease-out" />
                  <Line dataKey="profit" name="Profit" type="monotone" stroke="#10B981" strokeWidth={2.5}
                        dot={{ r: 3.5, fill: "#10B981", strokeWidth: 0 }} activeDot={{ r: 5 }} animationDuration={900} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-4 p-6 border-slate-100 rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.04)] bg-white" data-testid="recent-activities">
          <div className="text-[15px] font-display font-bold text-slate-900 mb-4">Recent Activity</div>
          <div className="space-y-4 max-h-64 overflow-y-auto scroll-thin pr-1">
            {data?.recent_activities?.length ? data.recent_activities.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 truncate">{a.text}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{a.user_name || a.user_email} · {fmtDate(a.created_at)}</div>
                </div>
              </div>
            )) : <div className="text-sm text-slate-500">No activities yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
